import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import { API_URL_batch_summary } from "../../constants";
import regression from 'regression'; // Import the regression library

const BatchSummary = () => {
  const [data, setData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const headers = getAuthHeaders();

    axios.get(API_URL_batch_summary, { headers })
      .then(response => {
        // Sort the data by settlement_time_utc to ensure proper ordering
        const sortedData = response.data.sort(
          (a, b) => new Date(a.settlement_time_utc) - new Date(b.settlement_time_utc)
        );
        setData(sortedData);
        setIsLoading(false);
      })
      .catch(error => {
        console.error(error);
        setErrorMessage('Error fetching batch summary data.');
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div>
        <h2>Batch Settlements Over Time</h2>
        <p>Loading data...</p>
      </div>
    );
  }

  // Ensure data is available and has at least one data point
  if (!data || data.length === 0) {
    return (
      <div>
        <h2>Batch Settlements Over Time</h2>
        <p>No data available.</p>
      </div>
    );
  }

  // Extract x and y values from data
  const xValues = data.map(d => new Date(d.settlement_time_utc));
  const yValues = data.map(d => Number(d.total_amount)); // Ensure yValues are numbers

  // Compute the average total amount
  const averageTotalAmount = yValues.reduce((sum, val) => sum + val, 0) / yValues.length;
  const averageLineYValues = yValues.map(() => averageTotalAmount);

  // Prepare data for regression
  // Normalize x-values to prevent numerical instability
  const xTimestamps = xValues.map(date => date.getTime());
  const minTimestamp = xTimestamps[0]; // Earliest timestamp
  const normalizedX = xTimestamps.map(timestamp => (timestamp - minTimestamp) / (1000 * 60 * 60 * 24)); // Convert to days since first date

  const regressionData = normalizedX.map((x, index) => [x, yValues[index]]);

  // Perform linear regression
  const result = regression.linear(regressionData);
  const trendLineYValues = normalizedX.map(x => result.predict(x)[1]);

  // Find all Sundays within the date range
  const startDate = new Date(Math.min(...xValues));
  const endDate = new Date(Math.max(...xValues));

  // Start from the first Sunday on or after startDate
  let firstSunday = new Date(startDate);
  firstSunday.setDate(firstSunday.getDate() + ((7 - firstSunday.getDay()) % 7));

  const sundayDates = [];
  for (let d = new Date(firstSunday); d <= endDate; d.setDate(d.getDate() + 7)) {
    sundayDates.push(new Date(d));
  }

  // Prepare vertical line shapes for Sundays
  const sundayShapes = sundayDates.map(date => ({
    type: 'line',
    x0: date,
    x1: date,
    y0: 0,
    y1: 1,
    xref: 'x',
    yref: 'paper',
    line: {
      color: 'grey',
      width: 1,
      dash: 'dashdot'
    }
  }));

  return (
    <div style={{ width: '100%', maxWidth: '100%', margin: '0 auto' }}>
      <h2>Batch Settlements Over Time</h2>
      {errorMessage && <p>{errorMessage}</p>}
      <div style={{ width: '100%', height: '600px' }}>
        <Plot
          data={[
            {
              x: xValues,
              y: yValues,
              type: 'scatter',
              mode: 'lines',
              fill: 'tozeroy',
              line: { shape: 'spline', color: 'blue' },
              name: 'Total Amount',
            },
            {
              x: xValues,
              y: averageLineYValues,
              type: 'scatter',
              mode: 'lines',
              line: { dash: 'dash', color: 'green' },
              name: 'Average Total Amount',
            },
            {
              x: xValues,
              y: trendLineYValues,
              type: 'scatter',
              mode: 'lines',
              line: { dash: 'dot', color: 'red' },
              name: 'Trend Line',
            },
          ]}
          layout={{
            title: 'Batch Settlements Over Time',
            xaxis: { title: 'Settlement Time (UTC)', type: 'date', automargin: true },
            yaxis: { title: 'Total Amount', automargin: true },
            shapes: sundayShapes,
            autosize: true,
            margin: { l: 50, r: 50, b: 80, t: 50, pad: 10 },
            legend: {
              orientation: 'h',
              yanchor: 'top',
              y: -0.2,
              xanchor: 'center',
              x: 0.5,
            },
          }}
          useResizeHandler={true}
          style={{ width: '100%', height: '100%' }}
          className="plotly-chart"
        />
      </div>
    </div>
  );
};

export default BatchSummary;